import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core';
import {
  DatePickerRangeComponent,
  DragAndDropImgUpload,
  Page,
  SelectImageButton
} from 'src/components';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  convertImageFileToBase64,
  dateToday,
  transformerDateField
} from 'src/utils';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { PromoProductListings } from '../component/PromoProductListing';
import usePromotional from 'src/hooks/promotional/use-promotional';
import { ErrorDialog } from 'src/components/error/ErrorDialog';
import usePrompt from 'src/utils/navigation-prompt';
import { useIsMobile } from 'src/hooks/use-is-mobile';
import { Autocomplete } from '@material-ui/lab';
import { slices, useAppDispatch } from 'src/redux';
import { unwrapResult } from '@reduxjs/toolkit';

const { actions: optionActions } = slices.options;

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  }
}));

export const PromotionalCreatePage = () => {
  const classes = useStyles();
  const {
    initialPromoProductListings,
    errMsgArr,
    showErrMsg,
    shouldPrompt,
    resetPromotionalDetails,
    setShowErrMsg,
    getPromotionalProductCreate,
    createPromotional,
    setShouldPrompt
  } = usePromotional();
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();

  const [fromDate, setFromDate] = useState<string | undefined>();
  const [toDate, setToDate] = useState<string | undefined>();
  const [banner, setBanner] = useState<any>();
  const [logoUrl, setLogoUrl] = useState<string>('');
  const [options, setOptions] = useState<string[]>([]);

  const formik = useFormik({
    initialValues: {
      title: '',
      banner_url: '',
      badge_url: '',
      app_name: '',
      start_date: '',
      end_date: '',
      is_publish: false,
      voucher_code: ''
    },
    validationSchema,
    onSubmit: () => {}
  });

  const hasFormErrors = useMemo(() => Object.keys(formik.errors).length > 0, [
    formik.errors
  ]); //check if there is errors in any fields

  const onChangedDate = useCallback(
    (date: MaterialUiPickersDate | undefined, field: 'from' | 'to') => {
      const transformedDate = date ? transformerDateField(date) : undefined;

      if (field === 'to') {
        setToDate(transformedDate);
        formik.setFieldValue('end_date', transformedDate);
      }
      if (field === 'from') {
        setFromDate(transformedDate);
        formik.setFieldValue('start_date', transformedDate);
      }
    },
    [formik]
  );

  const onChangeBanner = useCallback(
    async (img: File[]) => {
      if (img && img?.length > 0) {
        const firstFile = img[0];
        const imgInTxt: any = await convertImageFileToBase64(firstFile);
        setBanner(imgInTxt);
        formik.setFieldValue('banner_url', imgInTxt ? imgInTxt : '');
      }
    },
    [formik]
  );

  const onChangeBadge = async (img: File[]) => {
    if (img && img?.length > 0) {
      const firstFile = img[0];
      const imgInBase64: any = await convertImageFileToBase64(firstFile);
      setLogoUrl(imgInBase64);
      formik.setFieldValue('badge_url', imgInBase64);
    }
  };

  usePrompt(
    `There are some unsaved changes. Are you sure you want to leave?`,
    formik.values !== undefined && shouldPrompt
  );

  const onCreatePromotional = useCallback(async () => {
    if (hasFormErrors) {
      return;
    }
    if (
      formik.values &&
      initialPromoProductListings &&
      initialPromoProductListings?.data
    ) {
      const resultGetPromoProduct = getPromotionalProductCreate();
      resultGetPromoProduct.then((result) => {
        setShouldPrompt(false);
        const { voucher_code, ...rest } = formik.values;

        const payload = {
          ...rest,
          ...(voucher_code && voucher_code.trim() !== ''
            ? { voucher_code }
            : {}),
          products: result
        };

        createPromotional(payload);
      });

      return;
    }
  }, [
    hasFormErrors,
    formik.values,
    initialPromoProductListings,
    getPromotionalProductCreate,
    setShouldPrompt,
    createPromotional
  ]);

  useEffect(() => {
    resetPromotionalDetails();
  }, [resetPromotionalDetails]);

  // Get option app name
  const fetchOptions = useCallback(async () => {
    try {
      const response = unwrapResult(
        await dispatch(optionActions.getOptionsByIdSlug('app-names'))
      );
      if (response?.success) {
        const optionArray = response?.originalData?.data?.split(',');
        setOptions(optionArray);
      }
    } catch (error) {
      console.error('Error fetching options:', error);
    }
  }, [dispatch]);

  const handleAppNameChange = (
    event: React.ChangeEvent<{}>,
    newValue: string[]
  ) => {
    const sortedValues = [...newValue].sort();
    formik.setFieldValue('app_name', sortedValues.join(','));
  };

  useEffect(() => {
    fetchOptions();
  }, [dispatch, fetchOptions]);

  return (
    <Page title="Promotional" className={classes.root}>
      <Container maxWidth={false}>
        <form onSubmit={formik.handleSubmit}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', lg: 'row' }
            }}
          >
            <Typography color="textPrimary" gutterBottom variant="h3">
              Create Promo Information
            </Typography>
          </Box>

          <Box sx={{ mt: '1.5rem' }}>
            {banner && (
              <Box
                sx={{
                  padding: '1rem',
                  display: 'flex',
                  justifyContent: 'center',
                  p: '1rem'
                }}
              >
                <img
                  alt="Cover Image"
                  src={banner}
                  style={{
                    width: 900,
                    height: isMobile ? 100 : 300,
                    objectFit: 'contain'
                  }}
                />
              </Box>
            )}
            <DragAndDropImgUpload
              title="Drag or select photos for Banner"
              onImageSelected={onChangeBanner}
            />

            <Grid container spacing={3} style={{ marginTop: '1rem' }}>
              <Grid item xs={12} lg={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  id="title"
                  name="title"
                  label="Promo Title"
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.title && Boolean(formik.errors.title)}
                  helperText={formik.touched.title && formik.errors.title}
                />
              </Grid>
              <Grid item xs={12} lg={6}>
                <Box sx={{ display: 'flex' }}>
                  {logoUrl && (
                    <img
                      alt="promo-badge"
                      style={{ width: 150, height: 50, marginRight: '1rem' }}
                      src={logoUrl}
                    />
                  )}
                  <SelectImageButton
                    containerStyle={{ width: '100%' }}
                    title="Select Promo Badge"
                    multiple={false}
                    onImageSelected={onChangeBadge}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Autocomplete
                  onBlur={() => formik.setFieldTouched('app_name', true)}
                  options={options}
                  fullWidth
                  multiple
                  value={
                    formik.values.app_name
                      ? formik.values.app_name.split(',')
                      : []
                  }
                  onChange={handleAppNameChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="App Name"
                      variant="outlined"
                      error={
                        formik.touched.app_name &&
                        Boolean(formik.errors.app_name)
                      }
                      helperText={
                        formik.touched.app_name && formik.errors.app_name
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} lg={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  id="voucher_code"
                  name="voucher_code"
                  label="Voucher Code"
                  value={formik.values.voucher_code}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.voucher_code &&
                    Boolean(formik.errors.voucher_code)
                  }
                  helperText={
                    formik.touched.voucher_code && formik.errors.voucher_code
                  }
                />
              </Grid>
              <Grid
                item
                xs={12}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <DatePickerRangeComponent
                  title="Promo Duration"
                  fromDateMin={dateToday()}
                  fromDateValue={fromDate || null}
                  toDateValue={toDate || null}
                  dateToLabel="End Date"
                  dateFromLabel="Start Date"
                  hasNoDateToday
                  toDateMin={fromDate}
                  onChangeToDate={(date) => onChangedDate(date, 'to')}
                  onChangeFromDate={(date) => onChangedDate(date, 'from')}
                />
              </Grid>
            </Grid>

            <PromoProductListings />
          </Box>
        </form>
        <Box sx={{ width: '100%', p: 3 }}>
          <Button
            fullWidth
            color="primary"
            variant="contained"
            size="large"
            onClick={onCreatePromotional}
            disabled={
              !initialPromoProductListings?.data ||
              initialPromoProductListings?.data?.length <= 0 ||
              hasFormErrors
            }
          >
            Create Promotional
          </Button>
        </Box>
        <ErrorDialog
          errs={errMsgArr}
          open={showErrMsg}
          closeDialog={() => setShowErrMsg(false)}
        />
      </Container>
    </Page>
  );
};

const validationSchema = Yup.object({
  title: Yup.string()
    .required('Promo title is required')
    .min(2, 'Title is too short - should be 2 chars minimum.')
    .max(150, 'Title cannot exceed 150 characters')
    .test(
      'is-not-numeric',
      'This field must contain letters and cannot be only numbers',
      (value) => isNaN(Number(value))
    )
    .matches(
      /^[a-zA-Z0-9\s]+$/,
      'Title must contain only letters, numbers, and spaces'
    ),
  voucher_code: Yup.string()
    .min(10, 'Voucher code is too short - should be 15 chars minimum.')
    .max(15, 'Voucher code is too long - should be 15 characters only.'),
  app_name: Yup.string().required('App name is required')
});
